/**
 * Crash / refresh protection for the open clip (wave-5 review finding 2).
 *
 * A clip only exists in the library once the user saves it. Everything between "New clip" and
 * that first save lived in memory alone, so a refresh — or a closed tab, or a crash — took it.
 * This module keeps a single debounced *draft* in the `settings` store and offers it back on the
 * next boot.
 *
 * It holds no three.js and no DOM: a clip is plain data, so the whole thing is testable as a
 * settings round trip.
 */

import { normalizeClip } from '../shared';
import type { CanonicalAnimationClip, CanonicalBoneId } from '../shared';
import { settingsRepo } from '../persistence';
import type { Store } from './store';

/** `settings` key holding the draft of the open, unsaved clip. */
export const DRAFT_CLIP_KEY = 'draftClip';

/** How long a burst of keyframe edits is collected before the draft is rewritten. */
export const DRAFT_SAVE_DEBOUNCE_MS = 500;

export interface DraftClipRecord {
  /** The project that was open when the draft was written; `null` when there was none. */
  projectId: string | null;
  clip: CanonicalAnimationClip;
  savedAt: number;
}

export interface DraftAutosaveOptions {
  /** The project the draft belongs to, read at write time (it can change during a session). */
  projectId(): string | null;
  /** Debounce, in ms. Tests pass 0. */
  delay?: number;
  /**
   * Called whenever "is there an unwritten draft?" flips, one microtask late so it never
   * dispatches from inside the store subscription that triggered it.
   */
  onPendingChange?(pending: boolean): void;
}

export interface DraftAutosaveHandle {
  /** Write (or clear) the draft now, without waiting out the debounce. */
  flush(): Promise<void>;
  /** True while a draft write is on the debounce timer or in flight. */
  isPending(): boolean;
  dispose(): void;
}

/**
 * An empty, never-edited clip is not work worth restoring — "New clip" clears the draft.
 *
 * Event markers count as content. They are the only kind of work a clip can hold with no
 * keyframes at all, and "no content" here does not merely skip the write: `nextWrite` turns it
 * into an un-debounced `{kind:'clear'}`, so a new clip carrying three markers and no keys would
 * be DELETED on refresh, with no toast and no unload prompt.
 */
function hasContent(clip: CanonicalAnimationClip): boolean {
  for (const bone of Object.keys(clip.tracks) as CanonicalBoneId[]) {
    const track = clip.tracks[bone];
    if (track && track.keyframes.length > 0) return true;
  }
  return (clip.events?.length ?? 0) > 0;
}

function isDraftRecord(value: unknown): value is DraftClipRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Partial<DraftClipRecord>;
  const clip = record.clip as Partial<CanonicalAnimationClip> | undefined;
  return (
    !!clip &&
    typeof clip.id === 'string' &&
    typeof clip.name === 'string' &&
    typeof clip.fps === 'number' &&
    typeof clip.tracks === 'object' &&
    clip.tracks !== null
  );
}

export async function readDraftClip(): Promise<DraftClipRecord | null> {
  const stored = await settingsRepo.get<unknown>(DRAFT_CLIP_KEY, null);
  if (!isDraftRecord(stored)) return null;
  return {
    projectId: typeof stored.projectId === 'string' ? stored.projectId : null,
    clip: normalizeClip(stored.clip),
    savedAt: typeof stored.savedAt === 'number' ? stored.savedAt : 0,
  };
}

export async function clearDraftClip(): Promise<void> {
  await settingsRepo.remove(DRAFT_CLIP_KEY);
}

/**
 * Mirror the open clip into `settings` while it is dirty, and clear it as soon as it is saved
 * (or replaced by a fresh empty clip). Returns a handle the caller disposes with the app.
 */
export function installDraftAutosave(
  store: Store,
  options: DraftAutosaveOptions,
): DraftAutosaveHandle {
  const delay = options.delay ?? DRAFT_SAVE_DEBOUNCE_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // One writer at a time: a `set` overtaking a `remove` would resurrect a saved clip's draft.
  let chain: Promise<void> = Promise.resolve();
  let writes = 0;
  let reported = false;

  const pending = (): boolean => timer !== null || writes > 0;

  const syncPending = (): void => {
    const notifyPending = options.onPendingChange;
    if (!notifyPending) return;
    queueMicrotask(() => {
      const now = pending();
      if (now === reported) return;
      reported = now;
      notifyPending(now);
    });
  };

  const queue = (run: () => Promise<void>): Promise<void> => {
    writes++;
    chain = chain
      .then(run)
      .catch((error: unknown) => {
        console.warn('[intch-motion-kit] the clip draft could not be written', error);
      })
      .finally(() => {
        writes--;
        syncPending();
      });
    syncPending();
    return chain;
  };

  /**
   * What the next write would do, decided from the store alone: `clear` when nothing is open,
   * nothing is unsaved, or the open clip holds no work worth restoring — `set` otherwise.
   * One predicate, read by both `writeNow` (what to write) and `schedule` (whether to wait).
   */
  const nextWrite = (): { kind: 'clear' } | { kind: 'set'; clip: CanonicalAnimationClip } => {
    const state = store.getState();
    const clip = state.clip;
    if (!clip || !state.clipDirty || !hasContent(clip)) return { kind: 'clear' };
    return { kind: 'set', clip };
  };

  const writeNow = (): Promise<void> => {
    const next = nextWrite();
    if (next.kind === 'clear') {
      return queue(() => clearDraftClip());
    }
    const record: DraftClipRecord = {
      projectId: options.projectId(),
      clip: next.clip,
      savedAt: Date.now(),
    };
    return queue(() => settingsRepo.set(DRAFT_CLIP_KEY, record));
  };

  const schedule = (): void => {
    if (timer !== null) clearTimeout(timer);
    // A write that only DELETES the draft cannot lose user work, so there is nothing for a
    // debounce to protect — and delaying it is what made `ui.saving` (and with it the unload
    // prompt) claim "unsaved changes" for half a second after every successful save. Dirty
    // content keeps the debounce: that is the burst-of-edits case the mirror exists for.
    if (delay <= 0 || nextWrite().kind === 'clear') {
      // The cleared timeout must not stay non-null, or `pending()` would report a timer that
      // will never fire.
      timer = null;
      void writeNow();
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      void writeNow();
    }, delay);
    syncPending();
  };

  // Store listeners are synchronous and this only calls a repository — it dispatches nothing.
  const unsubscribeClip = store.select((state) => state.clip, schedule);
  const unsubscribeDirty = store.select((state) => state.clipDirty, schedule);

  return {
    async flush(): Promise<void> {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      await writeNow();
      await chain;
      syncPending();
    },
    isPending: pending,
    dispose(): void {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      unsubscribeClip();
      unsubscribeDirty();
      syncPending();
    },
  };
}

/**
 * Offer a stored draft back for `projectId`. Opens it, marks it dirty (it is still unsaved) and
 * toasts. Returns false when there is nothing to restore for that project.
 */
export async function restoreDraftClip(store: Store, projectId: string | null): Promise<boolean> {
  let draft: DraftClipRecord | null;
  try {
    draft = await readDraftClip();
  } catch (error) {
    console.warn('[intch-motion-kit] the clip draft could not be read', error);
    return false;
  }
  if (!draft || draft.projectId !== projectId) return false;

  store.batch(() => {
    store.actions.openClip(draft.clip);
    store.actions.markClipDirty();
    store.actions.toast('info', 'Restored unsaved clip draft.');
  });
  return true;
}
